import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, MagnifyingGlass, Star, Storefront } from "@phosphor-icons/react";
import api from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1517957096399-3a4e3d34d95e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

export default function Locali() {
  const [data, setData] = useState(null);
  const [citta, setCitta] = useState("");
  const [tipo, setTipo] = useState("Tutti");
  const [q, setQ] = useState({ citta: "", tipo: "" });
  const [showBar, setShowBar] = useState(false);

  const doSearch = (e) => {
    e.preventDefault();
    setQ({ citta, tipo: tipo === "Tutti" ? "" : tipo });
  };

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 420);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.title = "Prenota con Dishenda — Trova e prenota i migliori locali";
    return () => { document.title = "Dishenda"; };
  }, []);

  useEffect(() => {
    api.get("/marketplace/locali", { params: q })
      .then((r) => setData(r.data))
      .catch(() => setData({ locali: [], tipologie: [], citta: [] }));
  }, [q]);

  const tipologie = ["Tutti", ...(data?.tipologie || [])];
  const locali = data?.locali;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50" data-testid="marketplace-page"
      style={{ backgroundImage: "url(/vetrina/bg-linee.png)", backgroundRepeat: "repeat-y", backgroundPosition: "right top", backgroundAttachment: "fixed" }}>

      <nav className="fixed top-0 inset-x-0 z-40 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
          <Link to="/locali" className="flex items-center gap-3" data-testid="mk-logo">
            <img src="/vetrina/logo-dishenda.png" alt="Dishenda" className="h-14" />
            <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#2ce0cb] border border-[#2ce0cb]/40 rounded-full px-2.5 py-1">Prenota</span>
          </Link>
          <a href="/vetrina" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors" data-testid="mk-vetrina-link">Sei un ristoratore?</a>
        </div>
      </nav>

      <div className={`fixed top-20 inset-x-0 z-30 transition-[transform,opacity] duration-300 ${showBar ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0 pointer-events-none"}`} data-testid="mk-sticky-bar">
        <div className="max-w-2xl mx-auto px-5">
          <form onSubmit={doSearch} className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-700 rounded-full p-1.5 flex gap-1.5 shadow-2xl shadow-black/50">
            <label className="flex items-center gap-2 px-3 flex-1 min-w-0">
              <MapPin size={15} weight="duotone" className="text-[#2ce0cb] shrink-0" />
              <input value={citta} onChange={(e) => setCitta(e.target.value)} placeholder="Città o zona"
                className="bg-transparent w-full text-sm text-white placeholder-zinc-500 focus:outline-none h-9" data-testid="mk-sticky-citta" />
            </label>
            <button type="submit" className="h-9 px-4 rounded-full bg-[#2ce0cb] text-zinc-950 text-xs font-bold shrink-0 hover:bg-[#6ef0e0] transition-colors" data-testid="mk-sticky-btn">Cerca</button>
          </form>
        </div>
      </div>

      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-zinc-950/75" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/60 to-zinc-950/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pt-36 pb-16 sm:pt-44 sm:pb-24">
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tighter leading-[1.05] max-w-2xl" data-testid="mk-title">
            Trova il locale. <span className="text-[#2ce0cb]">Prenota il tavolo.</span> Fatto.
          </h1>
          <p className="mt-5 text-zinc-400 text-base sm:text-lg max-w-xl">
            Disponibilità reale, conferma immediata, recensioni di chi ha davvero mangiato lì.
          </p>
          <form onSubmit={doSearch}
            className="mt-9 bg-zinc-900/90 backdrop-blur border border-zinc-700 rounded-2xl p-3 max-w-2xl grid sm:grid-cols-[1fr_auto] gap-2" data-testid="mk-search">
            <label className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-xl px-4 h-12">
              <MapPin size={18} weight="duotone" className="text-[#2ce0cb]" />
              <input value={citta} onChange={(e) => setCitta(e.target.value)} placeholder="Città o zona (es. Treviso)"
                className="bg-transparent w-full text-sm text-white placeholder-zinc-500 focus:outline-none" data-testid="mk-search-citta" />
            </label>
            <button type="submit" className="h-12 px-7 rounded-xl bg-[#2ce0cb] text-zinc-950 text-sm font-bold hover:bg-[#6ef0e0] active:scale-[0.98] transition-[background-color,transform] flex items-center justify-center gap-2" data-testid="mk-search-btn">
              <MagnifyingGlass size={16} weight="bold" /> Cerca
            </button>
          </form>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="mk-chips">
            {tipologie.map((t) => (
              <button key={t} onClick={() => { setTipo(t); setQ({ citta, tipo: t === "Tutti" ? "" : t }); }}
                data-testid={`mk-chip-${t.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${tipo === t ? "bg-[#2ce0cb] text-zinc-950" : "bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-14">
        {!locali ? (
          <p className="text-center text-zinc-500 py-16" data-testid="mk-loading">Caricamento locali…</p>
        ) : locali.length === 0 ? (
          <div className="text-center py-16" data-testid="mk-empty">
            <Storefront size={44} weight="duotone" className="text-zinc-700 mx-auto" />
            <p className="font-display text-xl font-bold mt-4">Nessun locale trovato</p>
            <p className="text-sm text-zinc-500 mt-1">Prova a cambiare città o tipologia — nuovi locali arrivano ogni settimana.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mk-grid">
            {locali.map((l) => (
              <Link key={l.id} to={`/locali/${l.id}`} data-testid={`mk-card-${l.id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-zinc-600 group">
                {l.foto_url ? (
                  <img src={l.foto_url} alt={l.nome} className="h-48 w-full object-cover" />
                ) : (
                  <div className="h-48 w-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    <img src="/vetrina/logo-glyph.png" alt="" className="w-20 opacity-25" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-lg font-bold tracking-tight group-hover:text-[#2ce0cb] transition-colors">{l.nome}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">{[l.tipologia, l.citta].filter(Boolean).join(" · ") || "Locale"}</p>
                    </div>
                    {l.rating_avg && (
                      <span className="flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-bold text-amber-400 shrink-0" data-testid={`mk-rating-${l.id}`}>
                        <Star size={12} weight="fill" /> {l.rating_avg}
                      </span>
                    )}
                  </div>
                  {l.descrizione && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{l.descrizione}</p>}
                  {(l.tavoli_stasera > 0 || l.n_eventi > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {l.tavoli_stasera > 0 && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${l.tavoli_stasera <= 3 ? "bg-amber-400/10 text-amber-400 border-amber-400/30" : "bg-[#2ce0cb]/10 text-[#2ce0cb] border-[#2ce0cb]/30"}`} data-testid={`mk-live-${l.id}`}>
                          {l.tavoli_stasera <= 3 ? `Ultimi ${l.tavoli_stasera} slot stasera` : "Tavoli liberi stasera"}
                        </span>
                      )}
                      {l.n_eventi > 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-sky-400/10 text-sky-400 border border-sky-400/30" data-testid={`mk-eventi-${l.id}`}>
                          {l.n_eventi} {l.n_eventi === 1 ? "evento" : "eventi"} in programma
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-500 mt-2">
                    {l.rating_count > 0 ? `${l.rating_count} recensioni verificate` : "Nuovo nella vetrina"}
                    {l.fascia_prezzo ? ` · ${l.fascia_prezzo}` : ""}
                  </p>
                  <span className="mt-4 w-full h-10 rounded-full bg-[#2ce0cb] text-zinc-950 text-xs font-bold flex items-center justify-center group-hover:bg-[#6ef0e0] transition-colors">
                    Vedi e prenota
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-900 py-10 text-center text-xs text-zinc-500" data-testid="mk-footer">
        © 2026 Dishenda · Prenotazioni senza commissioni per coperto · <a href="/vetrina" className="underline hover:text-zinc-300">Sei un ristoratore?</a> · <a href="/api/seo/locali" className="underline hover:text-zinc-300">Indice dei locali</a> · <a href="/api/seo/sitemap.xml" className="underline hover:text-zinc-300">Sitemap</a>
      </footer>
    </div>
  );
}
